import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useNavigate } from "react-router-dom";
import { BottomNav } from "@/components/BottomNav";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Check, RefreshCw, Sun, Moon, ArrowUpRight, Repeat, Loader2 } from "lucide-react";
import api, { formatCurrency } from "@/lib/api";
import { toast } from "sonner";
import { useSSE } from "@/hooks/useSSE";
import { useSelectionQuote, selectionKey } from "@/hooks/useSelectionQuote";
import EmployeeReceivables from "./EmployeeReceivables";
import EmployeeAnticipations from "./EmployeeAnticipations";
import EmployeeProfile from "./EmployeeProfile";

export default function EmployeeApp() {
  const { user, logout, switchAccount } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [showAccounts, setShowAccounts] = useState(false);
  const [activeTab, setActiveTab] = useState("receivables");
  const [receivables, setReceivables] = useState([]);
  const [anticipations, setAnticipations] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Versão da BASE: incrementa a cada carga bem-sucedida de `/receivables`
  // (refresh manual, troca de aba ou evento SSE). A SELEÇÃO pode não mudar
  // enquanto a LISTA muda — sem versionar, a cotação anterior seguiria "fresca"
  // descrevendo recebível que já saiu, foi antecipado ou entrou em bloqueio.
  const [receivablesVersion, setReceivablesVersion] = useState(0);
  // Item 14a: a SELEÇÃO é recotada no backend (debounce + abort da anterior).
  // Fail-open: enquanto não volta — ou se falhar — a tela segue com o rateio.
  const { quote, quoteLoading, quoteError, quoteNow } =
    useSelectionQuote(selectedIds, receivablesVersion);
  const [showSummary, setShowSummary] = useState(false);
  // Cotação disparada PELO botão "Ver Resumo" (não pelo debounce) em voo.
  const [summaryPending, setSummaryPending] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(true);
  const [anticipationPct, setAnticipationPct] = useState(100);
  const [simulation, setSimulation] = useState(null);
  const [simulationError, setSimulationError] = useState(null);
  const [deductions, setDeductions] = useState(null);
  const [companies, setCompanies] = useState([]);

  const fetchReceivables = useCallback(async () => {
    try {
      const res = await api.get("/receivables");
      setReceivables(res.data.receivables || []);
      setAnticipationPct(res.data.anticipation_pct ?? 100);
      setSimulation(res.data.simulation || null);
      setSimulationError(res.data.simulation_error || null);
      setDeductions(res.data.deductions || null);
      setCompanies(res.data.companies || []);
      // Base nova ⇒ a cotação da seleção é recalculada e a antiga deixa de
      // contar como fresca (ver `useSelectionQuote`).
      setReceivablesVersion((v) => v + 1);
    } catch { toast.error("Erro ao carregar recebíveis"); }
  }, []);

  const fetchAnticipations = useCallback(async () => {
    try { const res = await api.get("/anticipations"); setAnticipations(res.data.items || res.data); } catch { toast.error("Erro ao carregar antecipações"); }
  }, []);

  useEffect(() => {
    Promise.all([fetchReceivables(), fetchAnticipations()]).finally(() => setLoading(false));
  }, [fetchReceivables, fetchAnticipations]);

  useSSE(useCallback((event, data) => {
    if (event === "anticipation_status" || event === "receivables_updated") {
      fetchReceivables();
      fetchAnticipations();
      if (event === "anticipation_status") {
        toast.info(`Antecipação atualizada: ${data?.status || ""}`);
      }
    }
  }, [fetchReceivables, fetchAnticipations]));

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        return next;
      }
      const target = receivables.find((r) => r.id === id);
      // Block selection for items in grace period (employee with < 2 months at company)
      if (target?.in_grace_period) {
        toast.error("Antecipação ainda não aprovada para este recebível");
        return prev;
      }
      // Block cross-company selection: each anticipation request must target a single company
      if (target && next.size > 0) {
        const anyOther = receivables.find((r) => next.has(r.id));
        if (anyOther && anyOther.company_id && target.company_id && anyOther.company_id !== target.company_id) {
          toast.error("Selecione recebíveis de uma empresa por vez");
          return prev;
        }
      }
      next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size > 0) { setSelectedIds(new Set()); return; }
    // Only consider receivables that are NOT in grace period AND NOT cutoff-blocked
    const eligible = receivables.filter((r) => !r.in_grace_period && !r.anticipation_blocked);
    // When multiple companies exist, select only the first company's receivables
    const firstCompany = eligible[0]?.company_id;
    const toSelect = firstCompany
      ? eligible.filter((r) => r.company_id === firstCompany).map((r) => r.id)
      : eligible.map((r) => r.id);
    setSelectedIds(new Set(toSelect));
  };

  const simMap = simulation?.receivables_map || {};
  // Cotação FRESCA = voltou para EXATAMENTE esta seleção, sobre ESTA versão da
  // base, e sem erro. `quote.ids` é a chave estável da seleção cotada
  // (`selectionKey`) e `quote.baseVersion` a versão da lista no momento da
  // cotação: os dois juntos impedem exibir número de outra seleção — ou da
  // mesma seleção sobre uma lista que já mudou.
  const currentSelectionKey = selectionKey(selectedIds);
  const quoteFresh = !!quote && quote.ids === currentSelectionKey
    && quote.baseVersion === receivablesVersion && !quoteError;
  const quoteMap = quoteFresh ? (quote.receivables_map || {}) : {};
  // Frescor POR LINHA: mapa parcial (o backend loga WARNING nesse caso) faz a
  // linha sem entrada cair no rateio — e ela tem de se marcar como aproximada,
  // não herdar a firmeza do agregado.
  const isRowQuoted = (r) => quoteFresh && quoteMap[r.id] != null;

  const getRecLiquid = (r) => {
    // 0. Re-cotação da seleção corrente (item 14a): o líquido da linha vem da
    //    cotação DESTA seleção, não do rateio do conjunto elegível inteiro.
    if (selectedIds.has(r.id)) {
      const q = quoteMap[r.id];
      if (q && typeof q.liquid_value === "number") return q.liquid_value;
    }
    // 1. If ZemoBank simulation has individual values, use them
    const sm = simMap[r.id];
    if (sm) return sm.liquid_value;
    // 2. If simulation exists with aggregated totals, distribute proportionally
    //    by each receivable's net_receivable share (accounts for date-based fees)
    if (simulation && simulation.total_bruto > 0 && simulation.total_liquido > 0) {
      const netRec = r.net_receivable || r.net_value || r.gross_value;
      const proportion = netRec / simulation.total_bruto;
      return Math.round(simulation.total_liquido * proportion * 100) / 100;
    }
    // 3. Fallback: use net_receivable (after deductions) × anticipation %
    return (r.net_receivable || r.net_value) * (anticipationPct / 100);
  };

  const selectedReceivables = receivables.filter((r) => selectedIds.has(r.id));
  const selectedSize = selectedIds.size;
  // Com cotação fresca o total é o AGREGADO do provider (não a soma das linhas
  // arredondadas): é o mesmo número que o resumo e o create vão usar.
  const totalLiquido = quoteFresh && typeof quote.total_liquido === "number"
    ? quote.total_liquido
    : selectedReceivables.reduce((s, r) => s + getRecLiquid(r), 0);

  // Snapshot SEMPRE atual (números + identidade juntos, do mesmo render) para o
  // fallback do resumo ler no momento da resposta em vez do closure.
  const summaryCtxRef = useRef(null);
  summaryCtxRef.current = {
    ids: currentSelectionKey,
    baseVersion: receivablesVersion,
    size: selectedSize,
    totalBruto: selectedReceivables.reduce((s, r) => s + (r.gross_value || 0), 0),
    totalLiquido,
  };

  const handleShowSummary = async () => {
    // Reentrância: o efeito de re-cotação pode disparar com uma requisição já
    // em voo (level-triggered) — `summaryPending` é o que impede empilhar.
    if (selectedIds.size === 0 || summaryPending) return;
    // A cotação desta seleção (e desta versão da base) já está na mão: abre o
    // resumo sem repetir o POST. `quote` já carrega `ids`/`baseVersion` — é o
    // SNAPSHOT do que o colaborador está vendo, e é contra ele que a
    // confirmação é conferida.
    if (quoteFresh) {
      setSummaryData(quote);
      setShowSummary(true);
      return;
    }
    // Segundo clique enquanto a cotação do resumo está em voo abortaria a
    // primeira (mesmo abort/seq) e piscaria o fallback local — o botão fica
    // desabilitado até esta resolver.
    setSummaryPending(true);
    try {
      // Sem cotação fresca: ANTECIPA o debounce em vez de abrir um POST
      // paralelo — mesmo abort/seq do hook, uma requisição só.
      let fresh = await quoteNow();
      // `null` = cancelada/superada/erro. Uma re-tentativa cobre o caso comum
      // (a cotação do debounce foi abortada por esta) sem virar laço.
      if (!fresh) fresh = await quoteNow();
      if (fresh) {
        setSummaryData(fresh);
        setShowSummary(true);
        return;
      }
      throw new Error("sem cotação");
    } catch {
      // Fallback LOCAL: rateio, não cotação. Continua confirmável (o produto
      // não pode travar num outage e o valor final é apurado pelo backend no
      // create), mas nasce rotulado como ESTIMATIVA — `approx: true` — e sem
      // taxa inventada.
      //
      // Números E identidade saem do MESMO snapshot, lido do ref no momento da
      // RESPOSTA: pelo closure, um bump de base durante o voo gravaria
      // `baseVersion` já defasado — o resumo se declararia fresco exibindo
      // número velho, ou ficaria eternamente stale sem nada redisparar.
      const ctx = summaryCtxRef.current;
      setSummaryData({
        total_bruto: ctx.totalBruto, total_liquido: ctx.totalLiquido,
        titulo_count: ctx.size, approx: true,
        ids: ctx.ids, baseVersion: ctx.baseVersion,
      });
      setShowSummary(true);
    } finally {
      setSummaryPending(false);
    }
  };

  // O resumo aberto descreve um CONJUNTO e uma VERSÃO da base. Se a seleção
  // mudar ou o `/receivables` recarregar (SSE) enquanto o diálogo está aberto,
  // o que está na tela deixa de descrever o que seria criado.
  const summaryStale = !!summaryData
    && (summaryData.ids !== currentSelectionKey
        || summaryData.baseVersion !== receivablesVersion);

  // Ref no padrão do `useSSE` (onEventRef): mantém a última versão da função
  // sem entrar como dependência do efeito.
  const showSummaryRef = useRef(null);
  showSummaryRef.current = handleShowSummary;

  useEffect(() => {
    if (!showSummary || !summaryStale) return;
    // Seleção esvaziada com o diálogo aberto: não há o que recotar.
    if (selectedSize === 0) { setShowSummary(false); return; }
    showSummaryRef.current?.();
    // LEVEL-triggered de propósito. Com deps só de `summaryStale`, uma rajada
    // de bumps (SSE) dentro de um round-trip deixava a flag `true` contínua: o
    // efeito não redisparava, e sobrava banner "recotando" permanente + botão
    // travado SEM requisição em voo. `receivablesVersion`/`currentSelectionKey`
    // fazem cada bump redisparar; `summaryPending` fecha o outro lado — a
    // chamada que caiu no guard de reentrância volta assim que a anterior
    // resolve, até convergir.
  }, [showSummary, summaryStale, selectedSize, receivablesVersion,
      currentSelectionKey, summaryPending]);

  const handleConfirm = async () => {
    // Fail-closed do CONSENTIMENTO: nunca criar conjunto diferente do exibido.
    // O botão já fica desabilitado nesse estado; isto fecha a corrida entre o
    // clique e uma atualização que chegue no meio.
    if (summaryStale) {
      toast.error("A seleção mudou — confira o novo resumo antes de confirmar.");
      return;
    }
    setConfirming(true);
    try {
      await api.post("/anticipations", { receivable_ids: Array.from(selectedIds) });
      toast.success("Antecipação solicitada com sucesso!");
      setShowSummary(false);
      setSelectedIds(new Set());
      await Promise.all([fetchReceivables(), fetchAnticipations()]);
      setActiveTab("anticipations");
    } catch (err) {
      if (err.response?.status === 422) {
        toast.error(err.response.data.detail || "Dados incompletos. Preencha seu Perfil antes de continuar.");
        setShowSummary(false);
        setActiveTab("profile");
      } else {
        toast.error(err.response?.data?.detail || "Erro ao solicitar antecipação");
      }
    }
    setConfirming(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center"><div className="loading-spinner w-8 h-8" /></div>
  );

  const numCompanies = companies.length || (receivables.length > 0 ? 1 : 0);
  const totalLiquidoHeader = companies.length > 0
    ? companies.reduce((s, c) => s + (c.simulation?.total_liquido || c.deductions?.net_receivable || 0), 0)
    : (simulation?.total_liquido || deductions?.net_receivable || 0);

  return (
    <div className="min-h-screen bg-background" data-testid="employee-app">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-lg mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-kredz-dark.png" alt="Kredz" className="h-8 w-auto" />
            <div>
              <p className="text-sm font-semibold">Olá, {user?.name?.split(" ")[0]}</p>
              <p className="text-[11px] text-muted-foreground">Colaborador</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" data-testid="switch-access-btn"
              onClick={async () => {
                if (showAccounts) { setShowAccounts(false); return; }
                try {
                  const res = await api.get("/auth/accounts");
                  const accs = res.data.accounts || [];
                  if (accs.length <= 1) { toast.info("Você possui apenas este acesso"); return; }
                  setAccounts(accs);
                  setShowAccounts(true);
                } catch { /* silent */ }
              }}
              className="text-muted-foreground hover:text-foreground">
              <Repeat className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={toggleTheme}
              data-testid="employee-theme-toggle" className="text-muted-foreground hover:text-foreground">
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => { fetchReceivables(); fetchAnticipations(); }}
              data-testid="refresh-btn" className="text-muted-foreground hover:text-foreground">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {showAccounts && accounts.length > 0 && (
          <div className="max-w-lg mx-auto px-5 pb-3 space-y-1 animate-fade-in" data-testid="account-switcher-mobile">
            {accounts.map((acc) => {
              const isCurrent = acc.id === user?.account_id;
              const routes = { admin: "/admin/dashboard", company_admin: "/company/dashboard", employee: "/employee" };
              return (
                <button key={acc.id} disabled={isCurrent}
                  onClick={async () => {
                    try {
                      await switchAccount(acc.id);
                      window.location.href = routes[acc.role] || "/";
                    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao trocar acesso"); }
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${isCurrent ? "bg-blue-500/10 border border-blue-500/20 text-blue-400" : "hover:bg-white/5 text-muted-foreground hover:text-foreground"}`}>
                  <span className="font-medium">{acc.label}</span>
                  {isCurrent && <span className="ml-2 text-[10px] text-blue-400/70">(atual)</span>}
                </button>
              );
            })}
          </div>
        )}
        {activeTab === "receivables" && numCompanies > 0 && (
          <div className="max-w-lg mx-auto px-5 pb-3" data-testid="header-summary-pill">
            <div className="flex items-center justify-between gap-3 px-3.5 py-2 rounded-xl bg-gradient-to-r from-emerald-500/15 via-emerald-500/5 to-blue-500/10 border border-emerald-500/20">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0 animate-pulse" aria-hidden />
                <span className="text-[11px] uppercase tracking-wider text-emerald-300/90 font-semibold shrink-0">Disponível</span>
              </div>
              <div className="flex items-center gap-2 text-right">
                <span className="text-sm font-bold text-emerald-300 tabular-nums" data-testid="header-total-liquido">
                  {formatCurrency(totalLiquidoHeader)}
                </span>
                {numCompanies > 1 && (
                  <span className="text-[10px] text-blue-300/90 bg-blue-500/15 border border-blue-500/25 px-2 py-0.5 rounded-full font-medium">
                    {numCompanies} empresas
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="max-w-lg mx-auto px-5 pb-safe-bottom">
        {activeTab === "receivables" && (
          <>
            <EmployeeReceivables
              receivables={receivables} selectedIds={selectedIds}
              toggleSelect={toggleSelect} toggleAll={toggleAll}
              anticipationPct={anticipationPct} simulation={simulation}
              simulationError={simulationError}
              getRecLiquid={getRecLiquid} deductions={deductions}
              quoteLoading={quoteLoading} isRowQuoted={isRowQuoted}
            />
            {selectedIds.size > 0 && (
              <div className="fixed bottom-20 left-0 right-0 z-40 px-5 animate-slide-up">
                <div className="max-w-lg mx-auto">
                  <div className="glass rounded-2xl p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                        {selectedIds.size} selecionado{selectedIds.size > 1 ? "s" : ""}
                        {quoteLoading && (
                          <>
                            <Loader2 data-testid="selection-quote-spinner"
                              className="w-3 h-3 animate-spin text-muted-foreground/70" aria-hidden />
                            <span className="sr-only">Recalculando o valor da seleção</span>
                          </>
                        )}
                      </p>
                      {/* "≈" = o número exibido é a estimativa local (rateio),
                          não a cotação desta seleção: em voo, com erro, ou
                          quando a lista mudou sob a cotação anterior. */}
                      <p data-testid="selection-total-liquido"
                        data-quote-state={quoteFresh ? "fresh" : quoteLoading ? "loading" : "approx"}
                        aria-live="polite"
                        className={`text-lg font-bold text-emerald-400 ${quoteLoading ? "animate-pulse" : ""}`}>
                        {quoteFresh ? "" : "≈ "}{formatCurrency(totalLiquido)}
                        {!quoteFresh && !quoteLoading && (
                          <span className="sr-only"> — valor aproximado</span>
                        )}
                      </p>
                    </div>
                    <Button data-testid="anticipate-btn" onClick={handleShowSummary}
                      disabled={summaryPending}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 h-11 rounded-xl">
                      {summaryPending ? (
                        <>Cotando <Loader2 className="w-4 h-4 animate-spin" aria-hidden /></>
                      ) : (
                        <>Ver Resumo <ArrowUpRight className="w-4 h-4" /></>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === "anticipations" && (
          <EmployeeAnticipations anticipations={anticipations} onRefresh={() => { fetchReceivables(); fetchAnticipations(); }} />
        )}

        {activeTab === "profile" && (
          <div className="py-6 pb-safe-bottom">
            <EmployeeProfile />
          </div>
        )}
      </main>

      <BottomNav activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Summary Dialog */}
      <Dialog open={showSummary} onOpenChange={setShowSummary}>
        <DialogContent className="sm:max-w-md border-white/10" data-testid="summary-dialog"
          data-summary-state={summaryStale ? "stale" : summaryData?.approx ? "approx" : "quoted"}>
          <DialogHeader>
            <DialogTitle className="text-xl">Resumo da Antecipação</DialogTitle>
            <DialogDescription>Confirme os valores antes de prosseguir</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-4">
            {/* Seleção/base mudaram sob o resumo aberto: o que está na tela não
                descreve mais o que seria criado — recotando, sem confirmar. */}
            {summaryStale && (
              <div data-testid="summary-stale-warning"
                className="flex items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-300 shrink-0" aria-hidden />
                <p className="text-[11px] text-amber-200">A seleção mudou — recotando antes de confirmar.</p>
              </div>
            )}
            <div className="bg-emerald-500/10 rounded-2xl p-6 text-center">
              <p className="text-xs text-emerald-400/70 mb-2 uppercase tracking-wider font-semibold">Valor Líquido a Receber</p>
              <p className="text-4xl font-bold text-emerald-400 tracking-tight" data-testid="summary-total-liquido">
                {summaryData?.approx ? "≈ " : ""}{formatCurrency(summaryData?.total_liquido)}
              </p>
              <p className="text-sm text-muted-foreground mt-2" data-testid="summary-total-bruto">
                Valor bruto: {formatCurrency(summaryData?.total_bruto)}
              </p>
              {/* Fallback local (outage do preview): honesto sobre o que é. */}
              {summaryData?.approx && (
                <p data-testid="summary-approx" className="text-[11px] text-amber-300/90 mt-3">
                  Valor estimado — o valor final é recalculado pelo sistema na confirmação.
                </p>
              )}
            </div>
            <div className="flex justify-center">
              <div className="bg-white/5 rounded-xl px-6 py-3 text-center">
                <p className="text-xs text-muted-foreground mb-0.5">Títulos selecionados</p>
                <p className="text-xl font-bold" data-testid="summary-titulo-count">{summaryData?.titulo_count}</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSummary(false)} className="border-white/10"
              data-testid="cancel-anticipation-btn">Cancelar</Button>
            <Button onClick={handleConfirm} disabled={confirming || summaryStale} data-testid="confirm-anticipation-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
              {confirming ? <div className="loading-spinner" /> : <><Check className="w-4 h-4 mr-1" /> Solicitar assinatura da Operação</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
